import React from 'react';
import { Form, reduxForm } from 'redux-form';
import { HashLoader } from 'react-spinners';
import { AfterEffectsClipTemplateViewModel, AfterEffectsMusicViewModel, AfterEffectsJobType } from 'stageclip-common';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';

import { SimpleModalId } from '../../../constants/modalId';
import AppModal from '../../modal/appModal';
import { AppFileField } from '../../form/appFileField';
import { AppSelectField } from '../../form/appSelectField';
import { AppTextField } from '../../form/appTextField';
import { maxLength, required, requiredString } from '../../form/formValidations';
import { ColorPickerFieldComponent } from '../../form/color-picker/color-picker-field.component';
import { ToggleFieldComponent } from '../../form/toggle/toggleField.component';
import {
  allowedAudioExtensions,
  allowedImageExtensions,
  allowedVideoExtensions,
  validateAudioFile,
  validateImageFile,
  validateVideoFile,
} from '../utils/fileValidators';
import styles from '../ClipGeneratorPage.module.scss';

import { CreateJobFormDataProps, CreateJobFormOnSubmitProps, CreateJobFormOwnProps } from './createJobForm.types';

const inputFieldValidator = maxLength(255);

const jobTypeOptions = [
  {
    value: AfterEffectsJobType.ClipJob,
    label: AfterEffectsJobType.ClipJob,
  },
  {
    value: AfterEffectsJobType.CeremonyJob,
    label: AfterEffectsJobType.CeremonyJob,
  },
];

const initialValues = {
  includeParticipantsImages: true,
  includeParticipantsMessageVideos: true,
  includeParticipantsLiveVideos: true,
  uploadNewMusicFile: false,
};

const generateTemplateOptions = (templates: AfterEffectsClipTemplateViewModel[]) =>
  templates.map(template => ({
    value: template.id,
    label: template.name,
  }));

const generateMusicOptions = (music: AfterEffectsMusicViewModel[]) =>
  music.map(musicEntity => ({
    value: musicEntity.fileUrl,
    label: musicEntity.name,
  }));

const CreateJobForm = ({
  form,
  handleSubmit,
  templates,
  onSubmit,
  isLoading,
  musicFiles,
  onMusicToggleChange,
  uploadCustomMusic,
}: CreateJobFormOwnProps) => {
  const handleOnSubmit = (values: CreateJobFormDataProps) => {
    const { uploadNewMusicFile, ...requestJobFormData } = values;

    return onSubmit(requestJobFormData);
  };
  const templateOptions = generateTemplateOptions(templates);
  const musicOptions = generateMusicOptions(musicFiles);

  return (
    <AppModal
      form={form}
      id={SimpleModalId.CreateJobModal}
      title="Create Job"
      buttonIcon={faPlus}
      hideActionButtons={isLoading}
      className={styles.modal}
    >
      {isLoading ? (
        <div className={styles.loader}>
          <HashLoader />
        </div>
      ) : (
        <Form id={form} onSubmit={handleSubmit(handleOnSubmit)}>
          <Tabs>
            <TabList>
              <Tab>Job Details & Assets</Tab>
              <Tab>Job Parameters</Tab>
            </TabList>
            <TabPanel>
              <AppSelectField
                name="jobType"
                key="jobType"
                label="Job Type"
                options={jobTypeOptions}
                validate={[required, requiredString]}
              />
              <AppSelectField
                name="afterEffectsClipTemplateId"
                key="afterEffectsClipTemplateId"
                label="Template"
                options={templateOptions}
                validate={[required, requiredString]}
              />
              <AppTextField name="schoolName" label="School Name" validate={[required, inputFieldValidator]} />
              <ColorPickerFieldComponent name="firstColor" label="Color 1" validate={required} />
              <ColorPickerFieldComponent name="secondColor" label="Color 2" validate={required} />
              <AppTextField name="hashTag" label="Hashtag" validate={[required, inputFieldValidator]} />
              <AppFileField
                name="schoolLogoImage"
                label="Logo"
                extensions={allowedImageExtensions}
                validate={[validateImageFile, required]}
              />
              <ToggleFieldComponent
                name="uploadNewMusicFile"
                onChange={onMusicToggleChange}
                label="Upload new music file"
              />
              {uploadCustomMusic ? (
                <AppFileField
                  name="music"
                  label="Music"
                  extensions={allowedAudioExtensions}
                  validate={[validateAudioFile].concat([uploadCustomMusic ? required : undefined].filter(e => !!e))}
                />
              ) : (
                <AppSelectField
                  name="music"
                  key="music"
                  label="Music"
                  options={musicOptions}
                  validate={[!uploadCustomMusic ? required : undefined].filter(e => !!e)}
                />
              )}
              <AppFileField
                name="preRollVideo"
                label="Pre-Roll Video"
                extensions={allowedVideoExtensions}
                validate={validateVideoFile}
              />
              <AppFileField
                name="postRollVideo"
                label="Post-Roll Video"
                extensions={allowedVideoExtensions}
                validate={validateVideoFile}
              />
            </TabPanel>
            <TabPanel>
              <ToggleFieldComponent name="includeParticipantsImages" label="Include participants' images?" />
              <ToggleFieldComponent
                name="includeParticipantsMessageVideos"
                label="Include participants' message videos?"
              />
              <ToggleFieldComponent name="includeParticipantsLiveVideos" label="Include participants' live videos?" />
            </TabPanel>
          </Tabs>
        </Form>
      )}
    </AppModal>
  );
};

export const CreateJobFormContainer = reduxForm<CreateJobFormDataProps, CreateJobFormOnSubmitProps>({
  form: 'createJobForm',
  initialValues,
  enableReinitialize: true,
})(CreateJobForm);
